import type { BundleItem } from "../../types";
import type { GoldMonthlyTitle } from "./types";

// ─── Title Helpers ──────────────────────────────────────────────────────────

export function isTitleClaimed(title: GoldMonthlyTitle): boolean {
  return title.claim_status === "claimed" || title.claim_status === "already_owned";
}

export function getTitleCoverUrl(title: GoldMonthlyTitle): string {
  if (title.type === "bundle" && title.bundle) {
    const firstItem = title.bundle.items?.[0];
    return firstItem?.thumbnail_url ?? firstItem?.comic_image_url ?? "";
  }
  if (title.release) {
    return title.release.thumbnail_url ?? title.release.image_url ?? "";
  }
  return "";
}

export function getTitleName(title: GoldMonthlyTitle): string {
  if (title.type === "bundle" && title.bundle) {
    return title.bundle.display_name || "Untitled";
  }
  if (title.release) {
    return title.release.title ?? title.release.localized_comic_name ?? "Untitled";
  }
  return "Untitled";
}

export function getTitlePublisher(title: GoldMonthlyTitle): string {
  if (title.type === "bundle" && title.bundle) {
    return title.bundle.owner?.name ?? "";
  }
  if (title.release) {
    return title.release.artist?.business_name ?? title.release.artist?.name ?? "";
  }
  return "";
}

/** Convert bundle items into BundleItem[] for BundleImageStack */
export function getBundleItemsForStack(title: GoldMonthlyTitle): BundleItem[] {
  if (title.type !== "bundle" || !title.bundle) return [];
  return title.bundle.items ?? [];
}

/** Get the read URL for a title (release read_url, or first bundle item) */
export function getTitleReadUrl(title: GoldMonthlyTitle): string | null {
  if (title.type === "bundle" && title.bundle) {
    const firstItem = title.bundle.items?.[0];
    return firstItem?.read_url ?? null;
  }
  if (title.release) {
    return title.release.read_url ?? null;
  }
  return null;
}

/**
 * Whether the user has any read progress on a claimed release. Bundles always
 * return false — designers want "Start reading" for bundles for now.
 */
export function hasReleaseReadProgress(title: GoldMonthlyTitle): boolean {
  if (title.type !== "release" || !title.release) return false;
  const status = title.release.user_read_status;
  if (!status) return false;
  return (status.read_progress_percentage ?? 0) > 0;
}

/** Get the description text for a title — falls back to editorial_note when empty. */
export function getTitleDescription(title: GoldMonthlyTitle): string {
  let description = "";
  if (title.type === "bundle" && title.bundle) {
    description = title.bundle.description ?? "";
  } else if (title.release) {
    description = title.release.description ?? "";
  }
  if (description.trim().length > 0) return description;
  return title.editorial_note?.trim() ?? "";
}

/** Sum total pageviews across all titles */
export function getTotalPageviews(titles: GoldMonthlyTitle[]): number {
  return titles.reduce((sum, title) => {
    if (title.type === "bundle" && title.bundle?.items) {
      return sum + title.bundle.items.reduce(
        (s, item) => s + (item.release_total_pageviews ?? 0), 0
      );
    }
    return sum + (title.release?.total_pageviews ?? 0);
  }, 0);
}

/** Format a number as a human-readable string (e.g. 3500000 -> "3.5M") */
export function formatViewCount(count: number): string {
  if (count >= 1_000_000) {
    const millions = count / 1_000_000;
    return `${millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)}M`;
  }
  if (count >= 1_000) {
    const thousands = count / 1_000;
    return `${thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(1)}K`;
  }
  return count.toString();
}
